import React, { ChangeEvent, ReactNode, useEffect, useRef, useState } from "react";
import { Box, Button, DialogActions, DialogContent, DialogTitle, Divider, Pagination, Stack, Typography, useTheme } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

import ResponsiveDialog from "components/dialogs/ResponsiveDialog";
import { ApiPagination, Tag } from "./TagManagementPage";
import APIRequest from "system/APIRequest";
import Chip from "components/uiElements/dataDisplay/Chip";
import ConfirmationDialog from "components/dialogs/ConfirmationDialog";
import { DashboardDocument } from "components/resources/dashboards/DashboardDocument";
import { ResourceTagEntry } from "./TagQueriesDialog";
import DashboardRow from "components/resources/dashboards/DashboardRow";
import TagRequestHelper from "./TagRequestHelper";
import { useTranslation } from "react-i18next";

interface TagDashboardsDialogProps {
  onClose(): void;
  onTagRemovedFromQuery(): void;
  tag: Tag;
}

const PaginationStart: ApiPagination = {
  cursor: "",
  page: 1,
  per_page: 10,
  total: 0
};

export default function TagDashboardsDialog(props: TagDashboardsDialogProps): React.JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();

  const [resourceTagEntries, setResourceTagEntries] = useState<Array<ResourceTagEntry>>([]);
  const [dashboards, setDashboards] = useState<Array<DashboardDocument>>([]);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const [pagination, setPagination] = useState<ApiPagination>({...PaginationStart});
  
  const selectedDashboard = useRef<DashboardDocument | null>(null);

  async function loadTagDashboards() {
    const result = await TagRequestHelper.getDashboardsForTag(props.tag.id);
    setResourceTagEntries(result.resourceTagEntries);
    setDashboards(result.dashboards);
  }

  async function removeTagFromDashboard(query: DashboardDocument) {
    const resourceTagEntry = resourceTagEntries.find((entry) => entry.resource_id == query.id);
    if (!resourceTagEntry) {
      return;
    }

    const response = await APIRequest(`obj/resource-tag-entry/${resourceTagEntry?.id}`, {}, {
      method: "DELETE"
    });

    const updatedDashboards = dashboards.filter((current) => current.id !== query.id);
    setDashboards(updatedDashboards);

    props.onTagRemovedFromQuery();
  }

  useEffect(() => {
    loadTagDashboards();
  }, [pagination.page]);

  function handlePageChanged(event: ChangeEvent<unknown>, value: number): void {
    setPagination({...pagination, page: value});
  }

  const tagQueryDashboard: ReactNode = dashboards.map((dashboard, index) => {
    return (
      <Stack key={`TagDashboardsDialog.${dashboard.id}`} direction="row" sx={{
        padding: 1
      }}>
        <Box sx={{
          flex: 1
        }}>
          <DashboardRow dashboard={dashboard} />
        </Box>
        <Box sx={{
          marginLeft: 1
        }}>
          <Button
            color="error"
            onClick={() => {
              selectedDashboard.current = dashboard;
              setShowDeleteConfirmation(true);
            }}
            size="small"
            variant="contained"
          >
            {i18n.t("tag-dashboards-dialog.remove-label")}
          </Button>
        </Box>
      </Stack>
    );
  }, [dashboards]);
  
  return (
    <ResponsiveDialog maxWidth="md" open={true}>
      <DialogTitle>
        <Stack direction="row" sx={{
          alignItems: "baseline"
        }}>
          <Chip label={props.tag.name} color={props.tag.color} />
          <Typography
            variant="h4"
            sx={{
              flex: 1,
              marginLeft: 0.5
            }}>{i18n.t("tag-dashboards-dialog.title")}</Typography>
          <Button 
            color="secondary"
            onClick={(event) => {
              event.stopPropagation();
              props.onClose();
            }}
            sx={{minWidth: 0, padding: 0}}
          >
            <CloseIcon />
          </Button>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack
          divider={<Divider />}
          sx={{
            border: 1,
            borderColor: theme.palette.grey[300],
            borderRadius: theme.general.borderRadius
          }}>
          {tagQueryDashboard}
        </Stack>
        {pagination.total > pagination.per_page &&
          <Stack
            sx={{
              alignItems: "center",
              padding: 1
            }}>
            <Pagination
              count={Math.ceil(pagination.total/pagination.per_page)}
              onChange={handlePageChanged}
              page={pagination.page}
              shape="rounded"
            />
          </Stack>
        }
        {showDeleteConfirmation &&
          <ConfirmationDialog
            confirmText={i18n.t("tag-dashboards-dialog.remove-confirmation.confirm-text")}
            description={i18n.t("tag-dashboards-dialog.remove-confirmation.description")}
            isOpen={showDeleteConfirmation}
            onClose={(confirmed) => {
              if (confirmed && selectedDashboard.current) {
                removeTagFromDashboard(selectedDashboard.current)
              }
              setShowDeleteConfirmation(false);
            }}
            title={i18n.t("tag-dashboards-dialog.remove-confirmation.title")}
          />
        }
      </DialogContent>
      <DialogActions>
        <Button
          color="secondary"
          onClick={(event) => {
            event.stopPropagation();
            props.onClose();
          }}
          size="small"
          variant="contained"
        >
          {i18n.t("tag-dashboards-dialog.action-close-label")}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}